import React from "react";
import "./showMore.css";

// Reusable mobile "show more / show less" primitives.
// Pattern: clamp a component's items on mobile, reveal the rest on tap.
// Keeps the page short on phones while showing everything on desktop/tablet.

// matchMedia hook — true when viewport is at/below `query` (default: phones).
export const useIsMobile = (query = "(max-width: 767px)") => {
  const [match, setMatch] = React.useState(
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
};

// Given an items array, returns the slice to render + toggle state, clamped
// only on mobile. Usage:
//   const { shown, isMobile, canToggle, open, toggle, hidden } = useClamp(items, 4);
export const useClamp = (items = [], initial = 4) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const clamp = isMobile && !open;
  return {
    isMobile,
    open,
    toggle: () => setOpen((o) => !o),
    setOpen,
    canToggle: isMobile && items.length > initial,
    hidden: Math.max(0, items.length - initial),
    shown: clamp ? items.slice(0, initial) : items,
  };
};

// Styled toggle button (centered).
export const MoreToggle = ({ open, onClick, moreLabel, lessLabel = "Show less" }) => (
  <div className="more-toggle-wrap">
    <button type="button" className="more-toggle" onClick={onClick} aria-expanded={open}>
      {open ? lessLabel : moreLabel}
      <svg className={open ? "is-open" : ""} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  </div>
);
